/* Telegram Web App API integration */

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            username?: string;
            first_name?: string;
            last_name?: string;
            language_code?: string;
          };
          query_id?: string;
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: {
          text: string;
          color: string;
          textColor: string;
          isVisible: boolean;
          isActive: boolean;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          setText: (text: string) => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
        };
        themeParams: Record<string, string>;
        colorScheme: "light" | "dark";
        isExpanded: boolean;
        viewportHeight: number;
        viewportStableHeight: number;
        headerColor: string;
        backgroundColor: string;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        HapticFeedback: {
          impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
          notificationOccurred: (type: "error" | "success" | "warning") => void;
          selectionChanged: () => void;
        };
      };
    };
  }
}

export const tg = window.Telegram?.WebApp;

/** Whether the app is running inside Telegram */
export const isTelegram = !!tg?.initData;

/** Extract user info from Telegram or return empty defaults */
export function getTelegramUser() {
  const user = tg?.initDataUnsafe?.user;
  return {
    id: user?.id ? String(user.id) : "",
    username: user?.username ?? "",
    firstName: user?.first_name ?? "",
    lastName: user?.last_name ?? "",
  };
}

/** Sync the stable viewport height into a CSS variable for layout use */
function syncViewportHeight() {
  if (!tg) return;
  const h = tg.viewportStableHeight || tg.viewportHeight || window.innerHeight;
  document.documentElement.style.setProperty("--tg-viewport-height", `${h}px`);
}

/** Initialize Telegram Web App — call once on mount */
export function initTelegram() {
  if (!tg) {
    // Not inside Telegram — mark body so CSS can branch if needed
    document.documentElement.dataset.telegram = "false";
    return;
  }

  document.documentElement.dataset.telegram = "true";
  document.documentElement.dataset.tgColorScheme = tg.colorScheme;

  // Signal readiness and expand to full height
  tg.ready();
  tg.expand();

  // Match Telegram chrome to app background for a seamless look
  try {
    tg.setHeaderColor("#ffffff");
    tg.setBackgroundColor("#ffffff");
  } catch {
    // Older Telegram clients may not support these — safe to ignore
  }

  // Initial viewport sync + listen for changes (keyboard open/close, resize)
  syncViewportHeight();
  const anyTg = tg as unknown as {
    onEvent?: (event: string, cb: () => void) => void;
  };
  anyTg.onEvent?.("viewportChanged", syncViewportHeight);
  anyTg.onEvent?.("themeChanged", () => {
    document.documentElement.dataset.tgColorScheme = tg.colorScheme;
  });
}

/** Show the Telegram MainButton */
export function showMainButton(text: string, onClick: () => void) {
  if (!tg) return;
  tg.MainButton.setText(text);
  tg.MainButton.onClick(onClick);
  tg.MainButton.show();
}

/** Hide the Telegram MainButton */
export function hideMainButton() {
  if (!tg) return;
  tg.MainButton.hide();
}

/** Haptic feedback shortcut */
export function haptic(type: "light" | "medium" | "heavy" = "light") {
  tg?.HapticFeedback?.impactOccurred(type);
}
